/**
 * ApiEndpointFormModal — criar/editar API endpoints.
 */

import React from 'react';
import { ApiEndpoint } from '../../../../types';
import { Button } from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Checkbox from '../../../../components/ui/Checkbox';
import Textarea from '../../../../components/ui/Textarea';
import Select from '../../../../components/ui/Select';
import Modal from '../../../../components/ui/Modal';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { notify } from '../../../../utils/toast';
import { useApiEndpointForm, HttpMethod } from '../hooks/useApiEndpointForm';
import AuthConfigSection from './AuthConfigSection';
import ActionsSelector from './ActionsSelector';

interface ApiEndpointFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'>) => void;
    endpointToEdit?: ApiEndpoint;
    generateToken: () => string;
    generateSlug: (name: string) => string;
    getEndpointUrl: (slug: string) => string;
}

const HTTP_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE'];

const ApiEndpointFormModal: React.FC<ApiEndpointFormModalProps> = ({
    isOpen,
    onClose,
    onSave,
    endpointToEdit,
    generateToken,
    generateSlug,
    getEndpointUrl
}) => {
    const { form, updateField, updateSlug, setHttpMethod, toggleAction, regenerateToken, getFormData, validate } = useApiEndpointForm({
        isOpen,
        endpointToEdit,
        generateToken,
        generateSlug
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onSave(getFormData());
        }
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(getEndpointUrl(form.slug));
            notify.success('URL copiada!');
        } catch {
            notify.error('Erro ao copiar');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <Modal.Header onClose={onClose}>
                {endpointToEdit ? 'Editar Endpoint' : 'Novo Endpoint'}
            </Modal.Header>
            <Modal.Body>
                <form id="endpoint-form" onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Nome *"
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder="Ex: Integração ERP"
                    />

                    <div className="input-wrap">
                        <Input
                            label="Slug *"
                            name="slug"
                            type="text"
                            required
                            value={form.slug}
                            onChange={(e) => updateSlug(e.target.value)}
                            placeholder="integracao-erp"
                        />
                        {form.slug && (
                            <div className="flex items-center gap-2">
                                <code className="flex-1 truncate rounded-md bg-surface-subtle px-2 py-1 text-xs text-secondary">
                                    {getEndpointUrl(form.slug)}
                                </code>
                                <button
                                    type="button"
                                    onClick={copyUrl}
                                    className="rounded-md p-1.5 text-muted transition-colors hover:bg-surface-subtle hover:text-primary"
                                    aria-label="Copiar URL"
                                >
                                    <AppIcon name="copy" className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    <Textarea
                        label="Descrição"
                        name="description"
                        value={form.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder="Descreva o propósito..."
                        minRows={2}
                        className="resize-none"
                    />

                    <AuthConfigSection
                        authType={form.authType}
                        authToken={form.authToken}
                        basicUsername={form.basicUsername}
                        basicPassword={form.basicPassword}
                        onAuthTypeChange={(type) => updateField('authType', type)}
                        onBasicUsernameChange={(v) => updateField('basicUsername', v)}
                        onBasicPasswordChange={(v) => updateField('basicPassword', v)}
                        onRegenerateToken={regenerateToken}
                    />

                    <Select
                        label="Método HTTP"
                        name="httpMethod"
                        value={form.httpMethod}
                        onChange={(e) => setHttpMethod(e.target.value as HttpMethod)}
                        options={HTTP_METHODS.map((method) => ({ value: method, label: method }))}
                    />

                    <ActionsSelector selectedActions={form.allowedActions} onToggleAction={toggleAction} />

                    <Checkbox
                        id="endpoint-active"
                        name="isActive"
                        label="Endpoint ativo"
                        checked={form.isActive}
                        onChange={(e) => updateField('isActive', e.target.checked)}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit" form="endpoint-form">
                    Salvar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ApiEndpointFormModal;
